package uz.mint.frontend.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import uz.mint.frontend.toSitePalette

/**
 * Filled button for the primary action in a dialog or section.
 *
 * Rendered disabled rather than hidden when [enabled] is false, so a customer can see that an
 * option exists and is currently unavailable instead of wondering where it went.
 */
@Composable
fun PrimaryButton(text: String, enabled: Boolean = true, onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Button(
        attrs = Modifier
            .padding(topBottom = 16.px, leftRight = 26.px)
            .fontWeight(FontWeight.Bold)
            .fontSize(16.px)
            // Disabled is a pale surface, not a fill of `rule` — see PrimaryAction in
            // LoginPage: `rule` is near-black in the light palette, so `ink2` on it was a
            // 1.29 contrast and the label vanished in one theme out of two.
            .border(if (enabled) 0.px else 2.px, if (enabled) LineStyle.None else LineStyle.Solid, palette.rule)
            .backgroundColor(if (enabled) palette.solidBg else palette.paper2)
            .color(if (enabled) palette.solidFg else palette.ink2)
            .opacity(if (enabled) 1.0 else 0.7)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .fontFamily("inherit")
            .onClick { if (enabled) onClick() }
            .toAttrs { if (!enabled) attr("disabled", "") }
    ) {
        SpanText(text)
    }
}

/**
 * Outlined button for actions that stay on the page.
 *
 * A real `<button>` rather than an anchor: retrying a failed request is not navigation, and an
 * `<a href="#">` would push a history entry and be announced as a link to nowhere.
 */
@Composable
fun SecondaryButton(text: String, onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Button(
        attrs = Modifier
            .padding(topBottom = 13.px, leftRight = 22.px)
            .fontWeight(FontWeight.Bold)
            .fontSize(15.px)
            .border(2.px, LineStyle.Solid, palette.rule)
            .backgroundColor(Colors.Transparent)
            .color(palette.ink)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .toAttrs()
    ) {
        SpanText(text)
    }
}
